// Original Overlay169 structured slide layer. Host and native share identical wrappers.
import React from 'react';
import type {LayoutBaseProps} from '../msb/layouts';
import {spring,interpolate,useVideoConfig} from 'remotion';
import {SLIDE_COMPONENTS} from '../msb/slide-components';
import {MOTION_STAGES} from '../msb/motion-stages';
import {slideWipe} from '../msb/motion';
import {CornerPeel,SpeedLines,slideRush} from './SlideAccents';
import {briefStageContent,briefGlueText} from '../msb/motion-brief-shape';
import {normalizeAccent} from '../../lib/motion-kit/oneshape/data';
import {SLIDE_COLORS_BY_SKIN,normHexColor,readableTextOn} from '../../lib/templates/overlay-caption-skin';
import {vmPanelTitle} from '../../lib/templates/vm-side-panel-title';
import {vmPanelLayout,vmPanelScrim} from './vm-side-panel-layout';
import {FW} from '../msb/tokens';
import {motionStageFromKey} from '../../lib/templates/motion-stage-family';
import {validSlideWordCues} from '../../lib/templates/word-cues';
import type {SourceSlide} from '../../../apps/precision-editor/protocol/source-slide';
/** '#RRGGBB' → [r,g,b] · 못 읽으면 null. */
const hexRgb=(v:unknown):[number,number,number]|null=>{const h=normHexColor(v);return h?[parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)]:null;};
export function Overlay169SlideLayer({data,f}:{data:SourceSlide;f:number}){
 const {width,height,fps}=useVideoConfig();
 const {context:c,slide:slideData,beats:slideBeats}=data;
 // ⏱🔤 낱말 신호(2026-10-04 · 선택 칸) — 없으면(정밀 편집·옛 페이로드) 종전 시간표 그대로.
 //   ★지문 검사★ — 정밀 편집에서 장표 글을 고치면 옛 신호가 남은 글에 붙는다 → 지금 글의 지문과 다르면 버린다.
 const wordCues=slideData?validSlideWordCues(data.wordCues,slideData.type,slideData.content):undefined,cueProps=wordCues?.items?{itemCues:wordCues.items}:{};
 const SlideComp=slideData?SLIDE_COMPONENTS[slideData.type] as React.ComponentType<LayoutBaseProps>:undefined;
 const isMotionBrief=c.motionBrief,slideMotionKit=c.motionKit,slideColors=c.colors;
 // 🧊🗂 모션 무대 가족 — 어느 무대로 그릴지는 표가 정한다(`motion-stage-family.ts`). 키가 없거나 모르는 값이면 모션 브리핑 무대(옛 페이로드·늦게 배포된 앱).
 const stageDef=motionStageFromKey(c.motionStage),{Stage:MotionStage,Exit:MotionStageExit,contentColors:stageContentColors}=MOTION_STAGES[stageDef.stage],stageAccent=SLIDE_COLORS_BY_SKIN[stageDef.skin]?.accent;
 const briefAccent=normalizeAccent(slideColors.accent)??stageAccent??slideColors.accent;
 const briefColors={...slideColors,accent:briefAccent};
 const briefBrand=normalizeAccent(slideColors.accent)?slideColors.accent:stageAccent??slideColors.accent;
 const briefCur=slideData?{type:slideData.type,content:briefStageContent(slideData.type,{...slideData.content,...(slideData.type==='kineticTypo'?{beatTexts:slideBeats.map(b=>b.text)}:{})})}:null;
 const prev=c.prevSlide;
 const briefPrev=prev?{type:prev.type,content:briefStageContent(prev.type,{...prev.content,...(prev.beatTexts?{beatTexts:prev.beatTexts}:{})})}:null;
 const briefExitPrev=isMotionBrief&&!slideData?briefPrev:null;
 const briefSceneFrames=c.sceneFrames,briefCaption=c.caption,briefPrevCaption=c.prevCaption;
 const presenterClipUrl=c.reserveRightPct>0,PRESENTER_RESERVE_PCT=c.reserveRightPct;
 const isFirstSlideScene=c.first,isLastSlideScene=c.last;
 const rounded=Math.max(0,Math.round(f));
 const rush=c.first?slideRush(rounded):null,ovOpacity=interpolate(spring({frame:f,fps,durationInFrames:16,config:{damping:13}}),[0,1],[0,1]);
 const wipe=slideMotionKit&&!isMotionBrief?slideWipe(rounded,fps):null,wipeWindow=!!wipe&&rounded<16;
 const slideKitProps=slideMotionKit||isMotionBrief?{motionKit:true}:{};
 const imgLocalFrame=f,activeSceneArrIdx=c.opening?0:1;
 return <>
      {slideData && SlideComp && isMotionBrief && (
        /* 🧊 모션 무대(표가 고른 무대 · 모션 브리핑 = 캔버스 + 변신하는 도형 하나 + 도형 안 장표). 앞 장면도 장표면 캔버스는 끊기지 않고,
           아니면 무대가 전환 길이로 캔버스를 밝힌다(MotionBriefStage). 딤·돌진·쓸림은 쓰지 않는다. */
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <MotionStage
            f={Math.max(0, Math.round(imgLocalFrame))}
            slide={briefCur ?? { type: slideData.type, content: slideData.content }}
            prevSlide={briefPrev}
            opening={activeSceneArrIdx === 0}
            sceneFrames={briefSceneFrames}
            canvas={slideColors.bg}
            brand={briefBrand}
            reserveRightPx={presenterClipUrl ? Math.round(width * PRESENTER_RESERVE_PCT / 100) : 0}
            captionTopPx={briefCaption?.top}
            captionBottomPx={briefCaption?.bottom}
            captionWidthPx={briefCaption?.width}
            prevCaption={briefPrevCaption}
            titleColors={briefColors ?? slideColors}
            sceneIndex={c.sceneIndex}
            wordCues={wordCues}
          >
            <SlideComp colors={stageContentColors(slideData.type, briefColors ?? slideColors, briefBrand, c.sceneIndex)} f={Math.max(0, Math.round(imgLocalFrame))} {...briefStageContent(slideData.type, slideData.content)} {...(slideBeats ? { beats: slideBeats.map((b) => ({ ...b, text: briefGlueText(b.text) })) } : {})} {...cueProps} {...slideKitProps} />
          </MotionStage>
        </div>
      )}
      {briefExitPrev && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <MotionStageExit f={Math.max(0, Math.round(imgLocalFrame))} prevSlide={briefExitPrev} canvas={slideColors.bg} brand={briefBrand}
            reserveRightPx={0} prevCaption={briefPrevCaption} split sceneIndex={c.sceneIndex} />
        </div>
      )}
      {slideData && SlideComp && !isMotionBrief && (
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          // 🚀 첫 장표 씬 도입 — 덩어리째 소실점에서 다가온다(0.8초 뒤 정확히 제자리).
          opacity: wipeWindow ? (rush ? rush.opacity : 1) : rush ? ovOpacity * rush.opacity : ovOpacity,
          ...(rush ? { transform: rush.transform } : {}),
        }}>
          {/* ④ kit — 장표 본문을 «아래에서 올라오는 창» 안에 둔다(창이 다 열리면 전체 화면 = 종전과 같은 자리). */}
          {(() => { const vm = c.vmPanel; const slideBody = vm ? (() => {
            /* 🎬🗂 영상 위 장표(VM) 한쪽 패널(2026-10-07 · `lib/templates/vm-side-panel.ts` 가 작업물 단위로 고른 장면만) —
               화면 전체 딤 대신 패널 쪽만 짙은 그라데이션 한 겹 · 장표는 축소한 가상 화면에 그대로(장표 10종 무수정) · 작은 색 꼬리표.
               프레젠터가 오른쪽을 쓰면 왼쪽 패널. 계산은 `vm-side-panel-layout.ts`(참고 영상 실측값). */
            const side = presenterClipUrl ? 'left' as const : 'right' as const;
            const shown = vmPanelTitle(slideData.content.title, vm.tag);
            const content = shown.title === slideData.content.title ? slideData.content : { ...slideData.content, title: shown.title };
            const L = vmPanelLayout({ width, height, side, hasTag: !!shown.tag, captionTop: vm.captionTop, slide: { type: slideData.type, content } });
            const rgb = hexRgb(slideColors.bg) ?? (slideColors.isDark === false ? [244, 247, 251] as [number, number, number] : [8, 10, 18] as [number, number, number]);
            return (<>
              <div data-vm-panel={side} style={{ position: "absolute", inset: 0, background: vmPanelScrim(side, rgb) }} />
              {L.tag && shown.tag ? (
                <div data-vm-panel-tag="" style={{ position: "absolute", left: L.tag.left, top: L.tag.top, padding: `${L.tag.padY}px ${L.tag.padX}px`, borderRadius: L.tag.radius,
                  background: slideColors.accent, color: readableTextOn(slideColors.accent, "#FFFFFF"), fontSize: L.tag.fontPx, fontWeight: FW.bold, lineHeight: 1.2, whiteSpace: "nowrap" }}>{shown.tag}</div>
              ) : null}
              <div data-vm-panel-frame="" style={{ position: "absolute", left: L.frame.left, top: L.frame.top, width: L.frame.width, height: L.frame.height, transform: `scale(${L.scale})`, transformOrigin: "0 0" }}>
                <SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...content} anchor={L.anchor} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />
              </div>
            </>);
          })() : (<>
          {/* 📐 설계도=딤 완화(회장님 07-17 검정 프리뷰 수리) — 브리핑극장 딤(0.84)은 밝은 실사 배경
              전제. 설계도의 어두운 결정론 배경 위에 겹치면 화면 전체가 죽는다(실측). 배경 그라디언트가
              은은히 비치는 수준으로만 눌러 장표 패널과 깊이를 분리한다. */}
          <div style={{ position: "absolute", inset: 0, background: c.dim }} />
          {presenterClipUrl ? (
            /* 🧑‍🏫 프레젠터 예약 컬럼(MSB composition 미러) — 장표 콘텐츠를 좌측 박스로 몰아 우측 아바타
               자리를 비운다(레이아웃 % 좌표는 이 박스 기준 재해석 → 겹침 없이 자연 축소). */
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: `${PRESENTER_RESERVE_PCT}%` }}>
              <SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...slideData.content} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />
            </div>
          ) : (
            <SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...slideData.content} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />
          )}
          </>);
          return wipe ? (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, top: `${((1 - wipe.p) * 100).toFixed(3)}%`, overflow: "hidden" }}>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height }}>{slideBody}</div>
            </div>
          ) : slideBody; })()}
          {wipe?.active && (
            /* ④ 쓸림 경계 띠 — 드러나는 선 바로 위를 액센트가 따라 올라간다(다 열리면 화면 위로 빠진다). */
            <div style={{ position: "absolute", left: 0, right: 0, height: Math.round(height * 0.03),
              top: Math.round((1 - wipe.p) * height - height * 0.03), background: slideColors.accent, opacity: 0.9 * wipe.bandOpacity }} />
          )}
          {/* 💨 속도선 — 돌진과 같은 구간에서만 살고 장표가 서기 전에 사라진다(글자 위에 남으면 소음). */}
          {isFirstSlideScene && (
            <SpeedLines f={Math.max(0, Math.round(imgLocalFrame))} color={slideColors.accent} width={width} height={height} />
          )}
          {/* 📄 모서리 접힘 — 마지막 장표 씬 우하단. 장표 위에 얹는 구두점. */}
          {isLastSlideScene && (
            <CornerPeel f={Math.max(0, Math.round(imgLocalFrame))} width={width} height={height}
              page={slideColors.panel} under={slideColors.bg} edge={slideColors.accent} />
          )}
        </div>
      )}
 </>;
}
